// Howyin Sounds — reel music library (CC-BY / CC0 tracks + creators' original
// sounds). Add tracks with their license + attribution, toggle availability,
// preview inline. Backed by /sound/adminList, createByAdmin, update, delete.
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { apiInstanceFetch } from "../../../util/api";
import { baseURL, secretKey } from "../../../util/config";
import { setToast } from "../../../util/toast";
import dayjs from "dayjs";

const LIMIT = 20;
const GOLD = "#F0C420";
const INK = "#1A1614";
const MUTED = "#8A857F";
const CATEGORIES = ["worship", "afrobeats", "hype", "chill", "love", "comedy", "traditional", "other"];
const LICENSES = ["cc-by", "cc0", "original", "licensed"];

const AddSoundModal = ({ onClose }) => {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [license, setLicense] = useState("cc-by");
  const [attribution, setAttribution] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [category, setCategory] = useState("other");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim() || !file) {
      setToast("error", "Title and an audio file are required.");
      return;
    }
    if (license === "cc-by" && !attribution.trim()) {
      setToast("error", "CC-BY tracks must include the full attribution line.");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("artist", artist.trim());
    fd.append("license", license);
    fd.append("attribution", attribution.trim());
    fd.append("sourceUrl", sourceUrl.trim());
    fd.append("category", category);
    fd.append("audio", file);
    try {
      const headers = { key: secretKey, Authorization: sessionStorage.getItem("token") };
      const res = await axios.post(`${baseURL}sound/createByAdmin`, fd, { headers });
      if (res.data?.status) {
        setToast("success", "Sound added.");
        onClose(true);
      } else {
        setToast("error", res.data?.message || "Failed.");
      }
    } catch (err) {
      setToast("error", err.message || "Failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.55)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header" style={{ background: GOLD, color: INK }}>
            <h5 className="modal-title" style={{ fontWeight: 700 }}>Add Sound</h5>
            <button type="button" className="btn-close" onClick={() => onClose(false)} />
          </div>
          <div className="modal-body">
            <label className="form-label mt-1">Title *</label>
            <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
            <label className="form-label mt-3">Artist</label>
            <input className="form-control" value={artist} onChange={(e) => setArtist(e.target.value)} />
            <div className="d-flex gap-2 mt-3">
              <div style={{ flex: 1 }}>
                <label className="form-label">License</label>
                <select className="form-select" value={license} onChange={(e) => setLicense(e.target.value)}>
                  {LICENSES.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label">Category</label>
                <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <label className="form-label mt-3">Attribution (required for CC-BY)</label>
            <textarea
              className="form-control"
              rows={2}
              placeholder={'e.g. "Track" Artist (site.com). Licensed under CC BY 4.0.'}
              value={attribution}
              onChange={(e) => setAttribution(e.target.value)}
            />
            <label className="form-label mt-3">Source URL</label>
            <input className="form-control" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} />
            <label className="form-label mt-3">Audio file * (mp3 / m4a / wav, max 25MB)</label>
            <input type="file" accept="audio/*" className="form-control" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div className="modal-footer">
            <button className="btn btn-light" onClick={() => onClose(false)} disabled={saving}>Cancel</button>
            <button className="btn" style={{ background: GOLD, color: INK, fontWeight: 700 }} onClick={save} disabled={saving}>
              {saving ? "Uploading…" : "Add Sound"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Sounds = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const fetchData = useCallback(() => {
    apiInstanceFetch
      .get(`sound/adminList?start=${page}&limit=${LIMIT}${q ? `&q=${encodeURIComponent(q)}` : ""}`)
      .then((res) => {
        if (res?.status) {
          setRows(res.sounds || []);
          setTotal(res.total || 0);
        }
      })
      .catch((err) => console.error(err));
  }, [page, q]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleActive = (row) => {
    apiInstanceFetch
      .patch(`sound/update?soundId=${row._id}`, { isActive: !row.isActive })
      .then((res) => {
        if (res?.status) {
          setRows((prev) => prev.map((r) => (r._id === row._id ? { ...r, isActive: !r.isActive } : r)));
        } else {
          setToast("error", res?.message || "Failed.");
        }
      })
      .catch(() => setToast("error", "Failed."));
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete "${row.title}"? Reels that used it keep their mixed audio.`)) return;
    fetch(`${baseURL}sound/delete?soundId=${row._id}`, {
      method: "DELETE",
      headers: { key: secretKey, Authorization: sessionStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((res) => {
        if (res?.status) {
          setToast("success", "Sound deleted.");
          setRows((prev) => prev.filter((r) => r._id !== row._id));
          setTotal((t) => Math.max(0, t - 1));
        } else {
          setToast("error", res?.message || "Delete failed.");
        }
      })
      .catch(() => setToast("error", "Delete failed."));
  };

  const uploaderName = (r) =>
    r.uploaderUserId
      ? `${r.uploaderUserId.firstName || ""} ${r.uploaderUserId.lastName || ""}`.trim() || r.uploaderUserId.email
      : "—";

  return (
    <div className="p-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h4 style={{ color: INK, fontWeight: 700, margin: 0 }}>Sounds · Reel Music Library</h4>
        <div className="d-flex gap-2">
          <input
            className="form-control"
            style={{ maxWidth: 220 }}
            placeholder="Search title / artist"
            value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }}
          />
          <button className="btn" style={{ background: GOLD, color: INK, fontWeight: 700, whiteSpace: "nowrap" }} onClick={() => setShowAdd(true)}>
            + Add Sound
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table align-middle">
          <thead>
            <tr style={{ color: MUTED }}>
              <th>Title</th>
              <th>Artist / Uploader</th>
              <th>Category</th>
              <th>License</th>
              <th>Length</th>
              <th>Uses</th>
              <th>Preview</th>
              <th>Added</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id}>
                <td style={{ fontWeight: 600, color: INK }} title={r.attribution || ""}>{r.title}</td>
                <td>{r.license === "original" ? uploaderName(r) : r.artist || "—"}</td>
                <td>{r.category}</td>
                <td>
                  <span className="badge" style={{ background: r.license === "original" ? "#e8e1d3" : GOLD, color: INK }}>
                    {r.license}
                  </span>
                </td>
                <td>{r.duration ? `${Math.floor(r.duration / 60)}:${String(r.duration % 60).padStart(2, "0")}` : "—"}</td>
                <td>{r.useCount || 0}</td>
                <td>
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <audio src={r.audio} controls preload="none" style={{ height: 30, maxWidth: 180 }} />
                </td>
                <td style={{ color: MUTED, fontSize: 13 }}>{dayjs(r.createdAt).format("DD MMM YYYY")}</td>
                <td>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" checked={!!r.isActive} onChange={() => toggleActive(r)} />
                  </div>
                </td>
                <td>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r)}>Delete</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="text-center py-4" style={{ color: MUTED }}>No sounds yet — click “+ Add Sound”.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex align-items-center justify-content-between">
        <span style={{ color: MUTED, fontSize: 13 }}>{total} sounds</span>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-light" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
          <span className="align-self-center" style={{ fontSize: 13 }}>Page {page} / {Math.max(1, Math.ceil(total / LIMIT))}</span>
          <button className="btn btn-sm btn-light" disabled={page >= Math.ceil(total / LIMIT)} onClick={() => setPage((p) => p + 1)}>Next ›</button>
        </div>
      </div>

      {showAdd && (
        <AddSoundModal
          onClose={(saved) => {
            setShowAdd(false);
            if (saved) fetchData();
          }}
        />
      )}
    </div>
  );
};

export default Sounds;
